import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Analytics } from '@vercel/analytics/react';
import './App.css';

// Initialize Supabase
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseKey);

const ESPN_API = 'https://site.web.api.espn.com/apis/site/v2/sports';
const SUMMARY_BASE = 'https://site.web.api.espn.com/apis/site/v2/sports';

// ESPN sport paths
const SPORTS = {
  NBA: '/basketball/nba',
  NFL: '/football/nfl',
  NHL: '/hockey/nhl',
  UFC: '/mma/ufc',
  NCAAB: '/basketball/mens-college-basketball',
  NCAAF: '/football/college-football',
  EPL: '/soccer/eng.1',
  UCL: '/soccer/uefa.champions',
  WC: '/soccer/fifa.world',
  MLB: '/baseball/mlb',
};

// Major college teams - only show NCAAB/Baseball games where at least one team is notable
// Focused on Power 4, Big East, and top mid-majors people actually watch
const POWER_TEAMS = [
  // SEC
  'Alabama', 'Arkansas', 'Auburn', 'Florida', 'Georgia', 'Kentucky', 'LSU', 'Mississippi State',
  'Missouri', 'Oklahoma', 'Ole Miss', 'South Carolina', 'Tennessee', 'Texas', 'Texas A&M', 'Vanderbilt',
  // Big Ten  
  'Illinois', 'Indiana', 'Iowa', 'Maryland', 'Michigan', 'Michigan State', 'Minnesota', 'Nebraska',
  'Northwestern', 'Ohio State', 'Oregon', 'Penn State', 'Purdue', 'Rutgers', 'UCLA', 'USC', 'Washington', 'Wisconsin',
  // Big 12
  'Arizona', 'Arizona State', 'Baylor', 'BYU', 'UCF', 'Cincinnati', 'Colorado', 'Houston',
  'Iowa State', 'Kansas', 'Kansas State', 'Oklahoma State', 'TCU', 'Texas Tech', 'Utah', 'West Virginia',
  // ACC
  'Boston College', 'Clemson', 'Duke', 'Florida State', 'Georgia Tech', 'Louisville', 'Miami',
  'North Carolina', 'NC State', 'Notre Dame', 'Pitt', 'Pittsburgh', 'Syracuse', 'Virginia', 'Virginia Tech', 'Wake Forest', 'SMU', 'California', 'Stanford',
  // Big East
  'Butler', 'UConn', 'Connecticut', 'Creighton', 'DePaul', 'Georgetown', 'Marquette', 'Providence',
  'Seton Hall', 'St. John\'s', "St. John's", 'Villanova', 'Xavier',
  // Top mid-majors that get national attention
  'Gonzaga', 'Saint Mary\'s', "Saint Mary's", 'San Diego State', 'Memphis', 'Dayton', 'VCU', 'Davidson', 'Nevada',
  // Baseball powerhouses (non-power conference)
  'Coastal Carolina', 'East Carolina', 'ECU', 'Wichita State', 'Dallas Baptist', 'DBU', 'Louisiana Tech',
  'Southern Miss', 'Louisiana', 'UL Lafayette', 'Tulane', 'Rice', 'San Diego', 'UC Irvine', 'Long Beach State',
  'Fresno State', 'Oregon State', 'Cal State Fullerton', 'CS Fullerton'
];

// Odds filtering range (40-60% = close games, adjust as needed)
const MIN_ODDS_PERCENT = 25;
const MAX_ODDS_PERCENT = 75;

// Featured games - bypass odds filter and show with special styling
// Add team names (partial match works) or event keywords
// IMPORTANT: Include sport to avoid cross-sport false matches (e.g., Miami baseball ≠ Miami football)
const FEATURED_GAMES = [
  { teams: ['Indiana', 'Miami'], sport: 'NCAAF', label: 'CFP National Championship 🏆' },
  // Add more featured games as needed:
  // { teams: ['Kansas City', 'Philadelphia'], sport: 'NFL', label: 'Super Bowl LVIII 🏈' },
  // { teams: ['Lakers', 'Celtics'], sport: 'NBA', label: 'NBA Finals Game 7 🏀' },
];

// Keywords that indicate a championship/important game (from ESPN event names)
const CHAMPIONSHIP_KEYWORDS = [
  'championship', 'final', 'super bowl', 'world series', 
  'stanley cup', 'playoffs', 'bowl game', 'cfp', 'national championship'
];

const SIGNUP_BONUS = 100;
const WIN_BASE_REWARD = 10;
const STREAK_BONUS_PER_LEVEL = 1;
const MAX_STREAK_BONUS = 20;

// Check if a college team is from a power conference or is a notable program
const isPowerTeam = (teamName) => {
  if (!teamName) return false;
  const normalized = teamName.toLowerCase();
  
  // Check each power team - must match as a complete word/phrase
  return POWER_TEAMS.some(team => {
    const teamLower = team.toLowerCase();
    
    // For multi-word teams (NC State, Boston College), use word boundary match
    if (team.includes(' ')) {
      const regex = new RegExp(`\\b${teamLower}\\b`, 'i');
      return regex.test(normalized);
    }
    
    // For single-word teams (Kansas, Duke, Indiana, etc):
    // The team name must be followed by a space then a mascot/nickname
    // OR be an exact match
    // This allows "Indiana Hoosiers" but blocks "Indiana State Sycamores"
    
    // Check if normalized starts with team + space
    if (normalized.startsWith(teamLower + ' ')) {
      // Make sure there's no other word between team and mascot
      // "Indiana Hoosiers" -> OK (Indiana + Hoosiers)
      // "Indiana State Sycamores" -> NOT OK (Indiana + State + Sycamores)
      const remainder = normalized.slice(teamLower.length + 1);
      // If remainder starts with a state/directional word, it's a different school
      const stateWords = ['state', 'tech', 'a&m', 'southern', 'northern', 'eastern', 'western', 'central'];
      const firstWord = remainder.split(' ')[0];
      if (stateWords.includes(firstWord)) {
        return false;
      }
      return true;
    }
    
    // Exact match
    return normalized === teamLower;
  });
};

// Capitalize first letter of username
const capitalizeUsername = (username) => {
  if (!username) return '';
  return username.charAt(0).toUpperCase() + username.slice(1);
};

export default function App() {
  // ── STATES ───────────────────────────────────────────────────────────────
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState('login');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const [games, setGames] = useState([]);
  const [allGamesUnfiltered, setAllGamesUnfiltered] = useState([]); // Store all games for Live/Final tabs
  const [todayGamesCache, setTodayGamesCache] = useState([]); // Always stores today's games for Live tab
  const [selectedSport, setSelectedSport] = useState('All');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  const [streakBalance, setStreakBalance] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [monthlyRecord, setMonthlyRecord] = useState({ wins: 0, losses: 0 });
  const [allTimeRecord, setAllTimeRecord] = useState({ wins: 0, losses: 0 });

  const [activePick, setActivePick] = useState(null); // single pick or first leg of parlay (for legacy compat)
  const [activePicks, setActivePicks] = useState([]); // pending legs only
  const [activeParlayAllLegs, setActiveParlayAllLegs] = useState([]); // ALL legs of in-progress parlay (pending + won)
  const [pickHistory, setPickHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);

  const [activeTab, setActiveTab] = useState('picks');

  const [showAuthModal, setShowAuthModal] = useState(false);

  const [selectedGame, setSelectedGame] = useState(null);
  const [boxScore, setBoxScore] = useState(null);
  const [boxLoading, setBoxLoading] = useState(false);
  const [showBoxModal, setShowBoxModal] = useState(false);
  const [useOddsFilter] = useState(true); // Enable odds filtering
  const [, setOddsData] = useState({});

  // Profile viewing state
  const [, setViewingProfile] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [profilePicks, setProfilePicks] = useState([]);
  const [profileMessages, setProfileMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // ── FUNCTION DEFINITIONS ─────────────────────────────────────────────────
  // Convert odds to implied probability percentage
  // Handles both American odds (e.g., -110, +150) and decimal odds (e.g., 1.91, 2.50)
  const oddsToImpliedProb = (odds) => {
    if (typeof odds !== 'number') {
      console.warn('Invalid odds value:', odds);
      return 50; // Default to 50% if invalid
    }
    
    // Check if it's decimal odds (typically between 1.0 and 10.0)
    if (odds >= 1.0 && odds <= 10.0) {
      // Decimal odds: probability = 1 / decimal * 100
      return (1 / odds) * 100;
    }
    
    // American odds format
    if (odds > 0) {
      // Positive odds: +150 means bet $100 to win $150
      // Implied prob = 100 / (odds + 100) * 100
      return (100 / (odds + 100)) * 100;
    } else {
      // Negative odds: -150 means bet $150 to win $100
      // Implied prob = |odds| / (|odds| + 100) * 100
      return (Math.abs(odds) / (Math.abs(odds) + 100)) * 100;
    }
  };

  // Check if game odds are within our filter range (close/competitive games)
  const isCloseGame = (homeOdds, awayOdds) => {
    if (!homeOdds || !awayOdds) return false;
    const homeProb = oddsToImpliedProb(homeOdds);
    const awayProb = oddsToImpliedProb(awayOdds);
    // Both teams should be between MIN and MAX percent
    return (homeProb >= MIN_ODDS_PERCENT && homeProb <= MAX_ODDS_PERCENT) &&
           (awayProb >= MIN_ODDS_PERCENT && awayProb <= MAX_ODDS_PERCENT);
  };

  // Fetch odds from Supabase cache (populated by Edge Function twice daily)
  const fetchOdds = async () => {
    console.log('Fetching odds from Supabase cache...');
    
    try {
      const { data, error } = await supabase
        .from('odds_cache')
        .select('odds_data, fetched_at')
        .eq('id', 'current')
        .single();

      if (error) {
        console.warn('Error fetching odds cache:', error.message);
        return [];
      }

      if (!data || !data.odds_data) {
        console.log('No cached odds data found');
        return [];
      }

      const cacheAge = Date.now() - new Date(data.fetched_at).getTime();
      const cacheAgeHours = Math.round(cacheAge / (1000 * 60 * 60) * 10) / 10;
      console.log(`Using cached odds (${cacheAgeHours} hours old, fetched at ${new Date(data.fetched_at).toLocaleString()})`);

      // Transform cached data to match expected format
      const oddsList = data.odds_data.map(item => ({
        sport: item.sport_label || item.sport,
        homeTeam: item.home_team,
        awayTeam: item.away_team,
        commenceTime: new Date(item.commence_time),
        homeOdds: item.home_odds,
        awayOdds: item.away_odds,
        homeProb: item.home_prob,
        awayProb: item.away_prob,
        isCloseGame: isCloseGame(item.home_odds, item.away_odds),
        bookmaker: item.bookmaker
      }));

      console.log(`Loaded ${oddsList.length} odds from cache, Close games: ${oddsList.filter(o => o.isCloseGame).length}`);
      return oddsList;

    } catch (e) {
      console.error('Error fetching odds cache:', e);
      return [];
    }
  };

  // Match ESPN game with odds data by team names and time
  const matchGameWithOdds = (game, oddsList) => {
    const gameDate = new Date(game.date);
    const gameSport = game.sport;
    
    // Normalize team names for matching (remove common suffixes/prefixes)
    const normalizeName = (name) => {
      if (!name) return '';
      return name.toLowerCase()
        .replace(/\s+(university|college|state|univ)$/i, '')
        .replace(/^(the\s+)/i, '')
        .replace(/\s+/g, ' ')
        .replace(/[^\w\s]/g, '') // Remove special characters
        .trim();
    };
    
    const gameHomeNorm = normalizeName(game.home.name);
    const gameAwayNorm = normalizeName(game.away.name);
    
    // Find matching odds
    for (const odds of oddsList) {
      if (odds.sport !== gameSport) continue;
      
      // Ensure oddsDate is a Date object
      const oddsDate = odds.commenceTime instanceof Date ? odds.commenceTime : new Date(odds.commenceTime);
      if (isNaN(oddsDate.getTime())) continue; // Skip invalid dates
      
      const sameDay = gameDate.toDateString() === oddsDate.toDateString();
      const timeDiff = Math.abs(gameDate - oddsDate);
      
      // Match if same day and within 6 hours (more lenient)
      if (sameDay && timeDiff < 6 * 60 * 60 * 1000) {
        const oddsHomeNorm = normalizeName(odds.homeTeam);
        const oddsAwayNorm = normalizeName(odds.awayTeam);
        
        // More flexible matching - check if names are similar
        const homeMatch1 = gameHomeNorm === oddsHomeNorm || gameHomeNorm === oddsAwayNorm;
        const homeMatch2 = gameHomeNorm.includes(oddsHomeNorm) || oddsHomeNorm.includes(gameHomeNorm);
        const homeMatch3 = gameHomeNorm.split(' ').some(word => word.length > 3 && oddsHomeNorm.includes(word)) ||
                          oddsHomeNorm.split(' ').some(word => word.length > 3 && gameHomeNorm.includes(word));
        
        const awayMatch1 = gameAwayNorm === oddsAwayNorm || gameAwayNorm === oddsHomeNorm;
        const awayMatch2 = gameAwayNorm.includes(oddsAwayNorm) || oddsAwayNorm.includes(gameAwayNorm);
        const awayMatch3 = gameAwayNorm.split(' ').some(word => word.length > 3 && oddsAwayNorm.includes(word)) ||
                          oddsAwayNorm.split(' ').some(word => word.length > 3 && gameAwayNorm.includes(word));
        
        // Match if both teams match (either order)
        const bothMatch = (homeMatch1 || homeMatch2 || homeMatch3) && (awayMatch1 || awayMatch2 || awayMatch3);
        
        if (bothMatch) {
          return odds;
        }
      }
    }
    
    return null;
  };

  const hashPassword = async (password) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(password + 'sftc_salt_2026');
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const loadUserData = async (userId) => {
    try {
      const { data: profile } = await supabase.from('profiles').select('*').eq('id', userId).single();
      if (profile) {
        setStreakBalance(profile.streak_balance || 0);
        setCurrentStreak(profile.current_streak || 0);
        setLongestStreak(profile.longest_streak || 0);
        setMonthlyRecord({ wins: profile.monthly_wins || 0, losses: profile.monthly_losses || 0 });
        setAllTimeRecord({ wins: profile.all_time_wins || 0, losses: profile.all_time_losses || 0 });
      }
      const { data: pendingPicksData } = await supabase.from('picks').select('*').eq('user_id', userId).eq('status', 'pending').order('created_at', { ascending: true });
      const allPending = pendingPicksData || [];
      setActivePicks(allPending);
      setActivePick(allPending[0] || null);

      // If pending picks share a parlay_id, fetch ALL legs of that parlay (including already-won ones)
      const parlayId = allPending.find(p => p.parlay_id)?.parlay_id;
      if (parlayId) {
        const { data: allParlayLegs } = await supabase.from('picks').select('*').eq('parlay_id', parlayId).order('created_at', { ascending: true });
        setActiveParlayAllLegs(allParlayLegs || allPending);
      } else {
        setActiveParlayAllLegs(allPending);
      }

      const { data: history } = await supabase.from('picks').select('*').eq('user_id', userId).neq('status', 'pending').order('created_at', { ascending: false }).limit(50);
      setPickHistory(history || []);
    } catch (e) {
      console.error('Error loading user data:', e);
    }
  };


  const fetchGames = async () => {
    setLoading(true);
    const allGames = [];
    const formatDate = (d) => `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    const dateStr = formatDate(selectedDate);
    const todayStr = formatDate(new Date());
    const isFetchingToday = dateStr === todayStr;

    // Fetch odds data from Supabase cache (populated by Edge Function twice daily)
    let currentOddsData = [];
    if (useOddsFilter) {
      currentOddsData = await fetchOdds();
      setOddsData(currentOddsData);
    }

    for (const [sport, path] of Object.entries(SPORTS)) {
      try {
        const res = await fetch(`${ESPN_API}${path}/scoreboard?dates=${dateStr}`);
        if (!res.ok) continue;
        const data = await res.json();
        if (!data.events) continue;
        data.events.forEach(event => {
          const eventDate = new Date(event.date);
          const comp = event.competitions?.[0];
          if (!comp) return;
          const home = comp.competitors?.find(c => c.homeAway === 'home');
          const away = comp.competitors?.find(c => c.homeAway === 'away');
          if (!home?.team || !away?.team) return;
          
          const homeName = home.team.displayName || home.team.name;
          const awayName = away.team.displayName || away.team.name;
          
          // Skip games with TBD teams
          if (homeName === 'TBD' || awayName === 'TBD' || 
              homeName.toLowerCase().includes('tbd') || awayName.toLowerCase().includes('tbd')) {
            console.log(`Skipping TBD game: ${awayName} @ ${homeName}`);
            return;
          }
          
          const eventName = (event.name || event.shortName || '').toLowerCase();
          
          // For NCAAB: Only include games with at least one power conference team
          if (sport === 'NCAAB') {
            const homeIsPower = isPowerTeam(homeName);
            const awayIsPower = isPowerTeam(awayName);
            if (!homeIsPower && !awayIsPower) {
              // Skip small conference matchups
              console.log(`Skipping small school game: ${awayName} @ ${homeName}`);
              return;
            }
          }
          
          // Check if this is a featured game (must match sport too)
          const featuredMatch = FEATURED_GAMES.find(fg => 
            (!fg.sport || fg.sport === sport) &&
            fg.teams.some(t => homeName.toLowerCase().includes(t.toLowerCase())) &&
            fg.teams.some(t => awayName.toLowerCase().includes(t.toLowerCase()))
          );
          
          // Check if event name contains championship keywords
          const isChampionship = CHAMPIONSHIP_KEYWORDS.some(kw => eventName.includes(kw));
          
          allGames.push({
            id: event.id,
            sport,
            date: eventDate,
            status: event.status?.type?.name || 'STATUS_SCHEDULED',
            statusDetail: event.status?.type?.shortDetail || '',
            featured: !!featuredMatch,
            featuredLabel: featuredMatch?.label || (isChampionship ? '🏆 Championship' : null),
            isChampionship: isChampionship || !!featuredMatch,
            home: {
              id: home.team.id,
              name: homeName,
              abbr: home.team.abbreviation,
              logo: home.team.logo,
              score: home.score || '0',
              winner: home.winner
            },
            away: {
              id: away.team.id,
              name: awayName,
              abbr: away.team.abbreviation,
              logo: away.team.logo,
              score: away.score || '0',
              winner: away.winner
            },
          });
        });
      } catch (e) {
        console.error(`Error fetching ${sport}:`, e);
      }
    }
    
    allGames.sort((a, b) => a.date - b.date);
    
    // Filter games if odds filtering is enabled and we have odds data
    if (useOddsFilter && Array.isArray(currentOddsData) && currentOddsData.length > 0) {
      console.log(`Filtering ${allGames.length} games with ${currentOddsData.length} odds entries...`);
      console.log(`Odds range: ${MIN_ODDS_PERCENT}% - ${MAX_ODDS_PERCENT}%`);
      
      // Match games with odds
      const gamesWithOdds = allGames.map(game => {
        const matchedOdds = matchGameWithOdds(game, currentOddsData);
        if (matchedOdds) {
          console.log(`Matched: ${game.away.name} @ ${game.home.name} - Odds: ${Math.round(matchedOdds.homeProb)}% vs ${Math.round(matchedOdds.awayProb)}% (Close: ${matchedOdds.isCloseGame})${game.featured ? ' ⭐ FEATURED' : ''}`);
        }
        return { ...game, odds: matchedOdds };
      });
      
      const matchedCount = gamesWithOdds.filter(g => g.odds).length;
      console.log(`Matched ${matchedCount} out of ${allGames.length} games with odds data`);
      
      // Filter to only close games (but always include NFL, NBA and featured/championship games)
      const filteredGames = gamesWithOdds.filter(game => {
        // Always show featured games regardless of odds
        if (game.featured || game.isChampionship) {
          console.log(`Including featured/championship game: ${game.away.name} @ ${game.home.name}`);
          return true;
        }
        
        // Always show NFL, NBA, EPL, UCL, MLB games regardless of odds (few games, all worth showing)
        if (game.sport === 'NFL' || game.sport === 'NBA' || game.sport === 'EPL' || game.sport === 'UCL' || game.sport === 'WC' || game.sport === 'MLB') {
          return true;
        }
        
        // For other sports, only show games that have matching odds AND are close games
        if (!game.odds) {
          return false;
        }
        
        // Recalculate isCloseGame with current MIN/MAX values (in case they changed)
        const homeProb = game.odds.homeProb;
        const awayProb = game.odds.awayProb;
        const isClose = (homeProb >= MIN_ODDS_PERCENT && homeProb <= MAX_ODDS_PERCENT) &&
                       (awayProb >= MIN_ODDS_PERCENT && awayProb <= MAX_ODDS_PERCENT);
        
        return isClose;
      });
      
      // Also include any priority sport games from allGames that weren't matched with odds
      const unmatchedPriorityGames = allGames.filter(game => 
        (game.sport === 'NFL' || game.sport === 'NBA' || game.sport === 'EPL' || game.sport === 'UCL' || game.sport === 'WC') && 
        !gamesWithOdds.find(g => g.id === game.id)
      );
      
      // Combine filtered games with unmatched priority games
      let filteredGamesList = [...filteredGames, ...unmatchedPriorityGames];
      
      // Sort so featured games appear first
      filteredGamesList.sort((a, b) => {
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
        if (a.isChampionship && !b.isChampionship) return -1;
        if (!a.isChampionship && b.isChampionship) return 1;
        return a.date - b.date;
      });
      
      console.log(`Filtered to ${filteredGamesList.length} games (${filteredGames.length} close/priority games + ${unmatchedPriorityGames.length} unmatched NFL/NBA games)`);
      
      // If no games match, show all games with a warning
      if (filteredGamesList.length === 0 && matchedCount > 0) {
        console.warn(`No games in ${MIN_ODDS_PERCENT}%-${MAX_ODDS_PERCENT}% range! Showing all matched games for debugging...`);
        const allMatched = gamesWithOdds.filter(g => g.odds);
        console.log('Showing all matched games:', allMatched.map(g => ({
          game: `${g.away.name} @ ${g.home.name}`,
          odds: g.odds ? `${Math.round(g.odds.homeProb)}% vs ${Math.round(g.odds.awayProb)}%` : 'none'
        })));
        setGames(allMatched);
        setAllGamesUnfiltered(allGames); // Store all games for Live/Final tabs
      } else if (filteredGamesList.length === 0 && matchedCount === 0) {
        console.warn('No games matched with odds data! This could mean:');
        console.warn('1. Team names from ESPN don\'t match Odds API team names');
        console.warn('2. No games scheduled for the selected date');
        console.warn('3. Odds API returned no data for selected sports');
        console.warn('Showing all games without odds filtering.');
        setGames(allGames);
        setAllGamesUnfiltered(allGames); // Store all games for Live/Final tabs
      } else {
        setGames(filteredGamesList);
        setAllGamesUnfiltered(allGames); // Store all games for Live/Final tabs
      }
    } else {
      // No odds data available - show all games
      if (useOddsFilter) {
        console.log('No odds data available yet - showing all games.');
      }
      setGames(allGames);
      setAllGamesUnfiltered(allGames);
    }

    // If we fetched today's games, update the today cache for the Live tab
    if (isFetchingToday) {
      setTodayGamesCache(allGames);
    } else {
      // We're browsing a different date — fetch today's games separately for Live tab
      try {
        const todayAllGames = [];
        for (const [sport, path] of Object.entries(SPORTS)) {
          try {
            const res = await fetch(`${ESPN_API}${path}/scoreboard?dates=${todayStr}`);
            if (!res.ok) continue;
            const data = await res.json();
            if (!data.events) continue;
            for (const event of data.events) {
              const comp = event.competitions?.[0];
              if (!comp) continue;
              const home = comp.competitors?.find(c => c.homeAway === 'home');
              const away = comp.competitors?.find(c => c.homeAway === 'away');
              if (!home?.team || !away?.team) continue;
              todayAllGames.push({
                id: event.id,
                sport,
                date: new Date(event.date),
                status: event.status?.type?.name || 'STATUS_SCHEDULED',
                statusDetail: event.status?.type?.detail || event.status?.type?.shortDetail || '',
                home: {
                  id: home.team.id,
                  name: home.team.displayName || home.team.name,
                  abbreviation: home.team.abbreviation,
                  score: home.score || '0',
                  logo: home.team.logo,
                },
                away: {
                  id: away.team.id,
                  name: away.team.displayName || away.team.name,
                  abbreviation: away.team.abbreviation,
                  score: away.score || '0',
                  logo: away.team.logo,
                },
                clock: event.status?.displayClock,
                period: event.status?.period,
              });
            }
          } catch (e) { /* ignore per-sport errors */ }
        }
        setTodayGamesCache(todayAllGames);
      } catch (e) {
        console.error('Failed to fetch today live games:', e);
      }
    }

    setLoading(false);
  };

  const calculateReward = (streak) => WIN_BASE_REWARD + Math.min(streak, MAX_STREAK_BONUS) * STREAK_BONUS_PER_LEVEL;

  const settleSinglePickResult = (pick, game) => {
    // Returns 'won', 'lost', 'push', or null (not finished)
    const isSoccerGame = game.sport === 'EPL' || game.sport === 'UCL' || game.sport === 'WC';
    const isFinished = game.status === 'STATUS_FINAL' ||
                       (isSoccerGame && game.statusDetail?.toUpperCase().includes('FT'));
    if (!isFinished) return null;

    const homeScore = parseInt(game.home.score) || 0;
    const awayScore = parseInt(game.away.score) || 0;
    const isTie = isSoccerGame && homeScore === awayScore;
    if (isTie) return 'push';

    let won;
    if (isSoccerGame) {
      const pickedHome = pick.picked_team_id === game.home.id;
      won = pickedHome ? homeScore > awayScore : awayScore > homeScore;
    } else {
      won = pick.picked_team_id === game.home.id ? game.home.winner : game.away.winner;
    }
    return won ? 'won' : 'lost';
  };

  const checkPickResult = async () => {
    if (activePicks.length === 0 || !user) return;

    const isParlay = activePicks.some(p => p.parlay_id);


    // Resolve result for each leg
    const legResults = activePicks.map(pick => {
      const game = allGamesUnfiltered.find(g => g.id === pick.game_id) || games.find(g => g.id === pick.game_id);
      if (!game) return { pick, result: null }; // not found yet
      return { pick, game, result: settleSinglePickResult(pick, game) };
    });

    // For parlays: void/push legs drop out; remaining must all finish and win
    // For singles: just settle directly
    if (!isParlay) {
      const { pick, game, result } = legResults[0];
      if (!game || result === null) return; // not finished

      await supabase.from('picks').update({
        status: result,
        settled_at: new Date().toISOString()
      }).eq('id', pick.id);

      if (result === 'push') {
        setActivePicks([]);
      setActiveParlayAllLegs([]);
        setActivePick(null);
        loadUserData(user.id);
        fetchLeaderboard();
        return;
      }

      const won = result === 'won';
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('current_streak, longest_streak, streak_balance, monthly_wins, monthly_losses, all_time_wins, all_time_losses')
        .eq('id', user.id)
        .single();
      if (!currentProfile) return;

      const dbStreak = currentProfile.current_streak || 0;
      let updateData;
      if (won) {
        const reward = calculateReward(dbStreak);
        const newStreak = dbStreak + 1;
        updateData = {
          current_streak: newStreak,
          longest_streak: Math.max(currentProfile.longest_streak || 0, newStreak),
          streak_balance: (currentProfile.streak_balance || 0) + reward,
          monthly_wins: (currentProfile.monthly_wins || 0) + 1,
          all_time_wins: (currentProfile.all_time_wins || 0) + 1,
        };
      } else {
        updateData = {
          current_streak: 0,
          monthly_losses: (currentProfile.monthly_losses || 0) + 1,
          all_time_losses: (currentProfile.all_time_losses || 0) + 1,
        };
      }
      await supabase.from('profiles').update(updateData).eq('id', user.id);
      setActivePicks([]);
      setActiveParlayAllLegs([]);
      setActivePick(null);
      loadUserData(user.id);
      fetchLeaderboard();
      return;
    }

    // PARLAY: process leg by leg
    // Settle any finished legs first
    for (const { pick, result } of legResults) {
      if (result !== null && pick.status === 'pending') {
        await supabase.from('picks').update({
          status: result === 'push' ? 'void' : result, // void dropped legs
          settled_at: new Date().toISOString()
        }).eq('id', pick.id);
      }
    }

    // Refresh the full parlay view (including just-settled legs) for the banner
    const sharedParlayId = activePicks.find(p => p.parlay_id)?.parlay_id;
    if (sharedParlayId) {
      const { data: freshLegs } = await supabase.from('picks').select('*').eq('parlay_id', sharedParlayId).order('created_at', { ascending: true });
      if (freshLegs) setActiveParlayAllLegs(freshLegs);
    }

    // Check if any leg lost → whole parlay loses immediately
    const anyLost = legResults.some(({ result }) => result === 'lost');
    if (anyLost) {
      // Mark remaining pending legs as lost too
      for (const { pick, result } of legResults) {
        if (result === null || result === 'won' || result === 'push') {
          await supabase.from('picks').update({ status: 'lost', settled_at: new Date().toISOString() }).eq('id', pick.id);
        }
      }
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('monthly_losses, all_time_losses')
        .eq('id', user.id).single();
      if (currentProfile) {
        await supabase.from('profiles').update({
          current_streak: 0,
          monthly_losses: (currentProfile.monthly_losses || 0) + 1,
          all_time_losses: (currentProfile.all_time_losses || 0) + 1,
        }).eq('id', user.id);
      }
      setActivePicks([]);
      setActiveParlayAllLegs([]);
      setActivePick(null);
      loadUserData(user.id);
      fetchLeaderboard();
      return;
    }

    // Check how many legs are still pending (not yet finished)
    const stillPending = legResults.filter(({ result }) => result === null || (result !== 'lost' && result !== 'void'));
    const wonLegs = legResults.filter(({ result }) => result === 'won');
    const voidLegs = legResults.filter(({ result }) => result === 'push');

    // Active legs = non-void, non-won
    const activeLegCount = legResults.length - voidLegs.length;
    const allActiveLegsWon = wonLegs.length >= activeLegCount && stillPending.filter(r => r.result === null).length === 0;

    if (!allActiveLegsWon) return; // Still waiting on some legs

    // ALL remaining legs won! Streak = number of winning legs
    const streakGain = wonLegs.length;
    const { data: currentProfile } = await supabase
      .from('profiles')
      .select('current_streak, longest_streak, streak_balance, monthly_wins, monthly_losses, all_time_wins, all_time_losses')
      .eq('id', user.id)
      .single();
    if (!currentProfile) return;

    const dbStreak = currentProfile.current_streak || 0;
    const reward = calculateReward(dbStreak) * streakGain;
    const newStreak = dbStreak + streakGain;
    await supabase.from('profiles').update({
      current_streak: newStreak,
      longest_streak: Math.max(currentProfile.longest_streak || 0, newStreak),
      streak_balance: (currentProfile.streak_balance || 0) + reward,
      monthly_wins: (currentProfile.monthly_wins || 0) + 1,
      all_time_wins: (currentProfile.all_time_wins || 0) + 1,
    }).eq('id', user.id);

    setActivePicks([]);
      setActiveParlayAllLegs([]);
    setActivePick(null);
    loadUserData(user.id);
    fetchLeaderboard();
  };

  // ADMIN: Bulk settle all pending picks from browser (bypasses ESPN server block)
  const bulkSettlePicks = async () => {
    const { data: pendingPicks } = await supabase
      .from('picks')
      .select('*')
      .eq('status', 'pending');

    if (!pendingPicks || pendingPicks.length === 0) {
      alert('No pending picks to settle!');
      return;
    }

    alert(`Settling ${pendingPicks.length} pending picks... check console for progress.`);
    let settled = 0, notFound = 0, notFinished = 0;

    // Group by sport+date to minimize ESPN calls
    const scoreboards = {};
    const sportPaths = {
      NBA: '/basketball/nba', NFL: '/football/nfl', NHL: '/hockey/nhl',
      NCAAF: '/football/college-football', NCAAB: '/basketball/mens-college-basketball',
      MLB: '/baseball/mlb', EPL: '/soccer/eng.1', UCL: '/soccer/uefa.champions',
      WC: '/soccer/fifa.world', UFC: '/mma/ufc'
    };

    for (const pick of pendingPicks) {
      const gameDate = new Date(pick.game_time);
      const dateStr = `${gameDate.getFullYear()}${String(gameDate.getMonth()+1).padStart(2,'0')}${String(gameDate.getDate()).padStart(2,'0')}`;
      const cacheKey = `${pick.sport}_${dateStr}`;

      if (!scoreboards[cacheKey]) {
        const path = sportPaths[pick.sport];
        if (!path) continue;
        try {
          const res = await fetch(`https://site.web.api.espn.com/apis/site/v2/sports${path}/scoreboard?dates=${dateStr}&limit=100`);
          const data = await res.json();
          scoreboards[cacheKey] = {};
          for (const event of (data.events || [])) {
            scoreboards[cacheKey][event.id] = {
              status: event.status?.type?.name,
              statusDetail: event.status?.type?.shortDetail || '',
              competitors: event.competitions?.[0]?.competitors || []
            };
          }
          console.log(`Fetched ${Object.keys(scoreboards[cacheKey]).length} ${pick.sport} games for ${dateStr}`);
        } catch (e) {
          console.error(`Failed to fetch ${cacheKey}:`, e);
          scoreboards[cacheKey] = {};
        }
      }

      const game = scoreboards[cacheKey]?.[pick.game_id];
      if (!game) { notFound++; continue; }

      const isSoccer = pick.sport === 'EPL' || pick.sport === 'UCL' || pick.sport === 'WC';
      const isFinished = game.status === 'STATUS_FINAL' || (isSoccer && game.statusDetail?.toUpperCase().includes('FT'));
      if (!isFinished) { notFinished++; continue; }

      const home = game.competitors.find((c) => c.homeAway === 'home');
      const away = game.competitors.find((c) => c.homeAway === 'away');
      if (!home || !away) continue;

      const homeScore = parseInt(home.score) || 0;
      const awayScore = parseInt(away.score) || 0;
      const isTie = isSoccer && homeScore === awayScore;

      let won = false;
      if (!isTie) {
        const pickedHome = pick.picked_team_id === home.team?.id ||
          (home.team?.displayName || '').toLowerCase().includes(pick.picked_team_name.toLowerCase().split(' ').pop() || '');
        won = pickedHome ? homeScore > awayScore : awayScore > homeScore;
      }

      const newStatus = isTie ? 'push' : (won ? 'won' : 'lost');
      await supabase.from('picks').update({ status: newStatus, settled_at: new Date().toISOString() }).eq('id', pick.id);

      if (!isTie) {
        const { data: profile } = await supabase.from('profiles').select('current_streak, longest_streak, streak_balance, monthly_wins, monthly_losses, all_time_wins, all_time_losses').eq('id', pick.user_id).single();
        if (profile) {
          const streak = profile.current_streak || 0;
          await supabase.from('profiles').update(
            won ? {
              current_streak: streak + 1,
              longest_streak: Math.max(profile.longest_streak || 0, streak + 1),
              streak_balance: (profile.streak_balance || 0) + Math.min(10 + streak, 30),
              monthly_wins: (profile.monthly_wins || 0) + 1,
              all_time_wins: (profile.all_time_wins || 0) + 1
            } : {
              current_streak: 0,
              monthly_losses: (profile.monthly_losses || 0) + 1,
              all_time_losses: (profile.all_time_losses || 0) + 1
            }
          ).eq('id', pick.user_id);
        }
      }

      settled++;
      console.log(`✅ ${pick.picked_team_name} → ${newStatus}`);
    }

    alert(`Done! Settled: ${settled}, Not found: ${notFound}, Not finished: ${notFinished}`);
    fetchLeaderboard();
  };

  const fetchLeaderboard = async () => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('username, current_streak, longest_streak, streak_balance, all_time_wins, all_time_losses')
        .order('current_streak', { ascending: false })
        .limit(25);

      setLeaderboard(
        data?.map(p => ({
          ...p,
          all_time_record: { wins: p.all_time_wins || 0, losses: p.all_time_losses || 0 }
        })) || []
      );
    } catch (e) {
      console.error('Error fetching leaderboard:', e);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    if (!username.trim() || !password.trim()) {
      setAuthError('Please enter both username and password');
      setAuthLoading(false);
      return;
    }

    try {
      const passwordHash = await hashPassword(password);
      const normalizedUsername = username.trim().toLowerCase();

      if (authMode === 'signup') {
        const { data: existing } = await supabase
          .from('profiles')
          .select('id')
          .eq('username', normalizedUsername)
          .single();

        if (existing) {
          setAuthError('Username already exists');
          setAuthLoading(false);
          return;
        }

        const { data: newUser, error } = await supabase
          .from('profiles')
          .insert({
            username: normalizedUsername,
            password_hash: passwordHash,
            current_streak: 0,
            longest_streak: 0,
            streak_balance: SIGNUP_BONUS,
            monthly_wins: 0,
            monthly_losses: 0,
            all_time_wins: 0,
            all_time_losses: 0
          })
          .select()
          .single();

        if (error) throw error;

        setUser({ id: newUser.id, username: newUser.username });
        setUsername(newUser.username);
        localStorage.setItem('sftc_session', JSON.stringify({ id: newUser.id, username: newUser.username }));
        setStreakBalance(SIGNUP_BONUS);
        setShowAuthModal(false);
        setPassword('');
        loadUserData(newUser.id);
      } else {
        const { data: existingUser, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('username', normalizedUsername)
          .eq('password_hash', passwordHash)
          .single();

        if (error || !existingUser) {
          setAuthError('Invalid username or password');
          setAuthLoading(false);
          return;
        }

        setUser({ id: existingUser.id, username: existingUser.username });
        setUsername(existingUser.username);
        localStorage.setItem('sftc_session', JSON.stringify({ id: existingUser.id, username: existingUser.username }));
        setShowAuthModal(false);
        setPassword('');
        loadUserData(existingUser.id);
      }
    } catch (err) {
      console.error('Auth error:', err);
      setAuthError(err.message || 'An error occurred');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('sftc_session');
    setActivePick(null);
    setActivePicks([]);
      setActiveParlayAllLegs([]);
    setActiveParlayAllLegs([]);

    setCurrentStreak(0);
    setLongestStreak(0);
    setStreakBalance(0);
    setMonthlyRecord({ wins: 0, losses: 0 });
    setAllTimeRecord({ wins: 0, losses: 0 });
    setUsername('');
    setPassword('');
    setPickHistory([]);
  };

  const isGameLocked = (game) => {
    // Soccer games with FT (Full Time) in statusDetail are finished
    const isSoccerFT = (game.sport === 'EPL' || game.sport === 'UCL' || game.sport === 'WC') && 
                       game.statusDetail?.toUpperCase().includes('FT');
    return game.status === 'STATUS_IN_PROGRESS' || game.status === 'STATUS_FINAL' || isSoccerFT;
  };

  const isPickLocked = () => {
    if (activePicks.length === 0) return false;
    // Any leg started = locked
    return activePicks.some(pick => {
      // Search all game sources including todayGamesCache (always has fresh live data)
      const game = todayGamesCache.find(g => g.id === pick.game_id)
        || allGamesUnfiltered.find(g => g.id === pick.game_id)
        || games.find(g => g.id === pick.game_id);
      if (game) return isGameLocked(game);
      // Fallback: if game_time is in the past by more than 10 min, treat as locked
      if (pick.game_time) {
        return new Date(pick.game_time) < new Date(Date.now() - 10 * 60 * 1000);
      }
      return false;
    });
  };



  // Toggle a selection in the pending parlay slip
  const handlePick = async (game, team, isHome) => {
    if (!user || isGameLocked(game)) return;
    if (activePicks.length > 0 && isPickLocked()) return;

    // Clicking an already-active leg → remove it (unless locked)
    const existingActiveLeg = activePicks.find(p => p.game_id === game.id);
    if (existingActiveLeg) {
      if (isPickLocked()) return;
      // If switching teams on same game, replace; if same team, deselect
      if (existingActiveLeg.picked_team_id === team.id) {
        // Deselect: delete this leg, re-assign parlay_id if needed
        await supabase.from('picks').delete().eq('id', existingActiveLeg.id);
        const remaining = activePicks.filter(p => p.game_id !== game.id);
        // If only 1 leg remains, clear its parlay_id (it's now a single)
        if (remaining.length === 1 && remaining[0].parlay_id) {
          await supabase.from('picks').update({ parlay_id: null }).eq('id', remaining[0].id);
          remaining[0] = { ...remaining[0], parlay_id: null };
        }
        setActivePicks(remaining);
        setActivePick(remaining[0] || null);
        setActiveParlayAllLegs(remaining);
      } else {
        // Switch team: update in place
        const { data: updated } = await supabase
          .from('picks')
          .update({ picked_team_id: team.id, picked_team_name: team.name, opponent_team_name: isHome ? game.away.name : game.home.name })
          .eq('id', existingActiveLeg.id)
          .select().single();
        if (updated) {
          const newActive = activePicks.map(p => p.id === existingActiveLeg.id ? updated : p);
          setActivePicks(newActive);
          setActivePick(newActive[0] || null);
          setActiveParlayAllLegs(newActive);
        }
      }
      return;
    }

    // Max 4 legs
    if (activePicks.length >= 4) {
      alert('Maximum 4 legs in a parlay.');
      return;
    }

    // Adding a new leg — assign shared parlay_id if this will be leg 2+
    let parlayId = activePicks.length > 0 ? (activePicks[0].parlay_id || null) : null;
    if (activePicks.length === 1 && !parlayId) {
      parlayId = crypto.randomUUID();
      await supabase.from('picks').update({ parlay_id: parlayId }).eq('id', activePicks[0].id);
      activePicks[0] = { ...activePicks[0], parlay_id: parlayId };
    } else if (activePicks.length >= 2 && !parlayId) {
      parlayId = activePicks[0].parlay_id;
    }

    const { data: newPick, error } = await supabase
      .from('picks')
      .insert({
        user_id: user.id,
        game_id: game.id,
        sport: game.sport,
        picked_team_id: team.id,
        picked_team_name: team.name,
        opponent_team_name: isHome ? game.away.name : game.home.name,
        game_time: game.date.toISOString(),
        status: 'pending',
        parlay_id: parlayId,
      })
      .select().single();

    if (error) {
      console.error('Pick error:', error);
    } else if (newPick) {
      const newActive = [...activePicks, newPick];
      setActivePicks(newActive);
      setActivePick(newActive[0]);
      setActiveParlayAllLegs(newActive);
    }
  };

  const cancelPick = async () => {
    if (activePicks.length === 0) return;
    // If any leg is locked (game started), settle as loss — no escape from a live pick
    if (isPickLocked()) {
      const now = new Date().toISOString();
      for (const ap of activePicks) {
        await supabase.from('picks').update({ status: 'lost', settled_at: now }).eq('id', ap.id);
      }
      // Update profile: streak reset, +1 loss
      if (user) {
        const { data: profile } = await supabase.from('profiles')
          .select('monthly_losses, all_time_losses')
          .eq('id', user.id).single();
        if (profile) {
          await supabase.from('profiles').update({
            current_streak: 0,
            monthly_losses: (profile.monthly_losses || 0) + 1,
            all_time_losses: (profile.all_time_losses || 0) + 1,
          }).eq('id', user.id);
        }
        setCurrentStreak(0);
      }
      setActivePicks([]);
      setActiveParlayAllLegs([]);
      setActivePick(null);
      setActiveParlayAllLegs([]);
      return;
    }
    // Game hasn't started — safe to delete
    for (const ap of activePicks) {
      await supabase.from('picks').delete().eq('id', ap.id);
    }
    setActivePicks([]);
      setActiveParlayAllLegs([]);
    setActivePick(null);
    setActiveParlayAllLegs([]);
  };

  // Profile viewing functions
  const openProfile = async (profileUsername) => {
    setProfileLoading(true);
    setShowProfileModal(true);
    setViewingProfile(profileUsername);
    setProfileData(null);
    setProfilePicks([]);
    setProfileMessages([]);

    try {
      // Fetch profile data
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, username, current_streak, longest_streak, streak_balance, monthly_wins, monthly_losses, all_time_wins, all_time_losses, created_at')
        .eq('username', profileUsername.toLowerCase())
        .single();

      if (profile) {
        setProfileData(profile);

        // Fetch their pick history
        const { data: picks } = await supabase
          .from('picks')
          .select('*')
          .eq('user_id', profile.id)
          .neq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(50);

        setProfilePicks(picks || []);

        // Fetch profile messages (guestbook)
        const { data: messages } = await supabase
          .from('profile_messages')
          .select('*')
          .eq('profile_id', profile.id)
          .order('created_at', { ascending: false })
          .limit(50);

        setProfileMessages(messages || []);
      }
    } catch (e) {
      console.error('Error loading profile:', e);
    } finally {
      setProfileLoading(false);
    }
  };

  const closeProfile = () => {
    setShowProfileModal(false);
    setViewingProfile(null);
    setProfileData(null);
    setProfilePicks([]);
    setProfileMessages([]);
    setNewMessage('');
  };

  const submitMessage = async () => {
    if (!user || !profileData || !newMessage.trim()) return;

    try {
      const { error } = await supabase.from('profile_messages').insert({
        profile_id: profileData.id,
        author_id: user.id,
        author_username: user.username,
        message: newMessage.trim()
      });

      if (!error) {
        // Refresh messages
        const { data: messages } = await supabase
          .from('profile_messages')
          .select('*')
          .eq('profile_id', profileData.id)
          .order('created_at', { ascending: false })
          .limit(50);

        setProfileMessages(messages || []);
        setNewMessage('');
      }
    } catch (e) {
      console.error('Error posting message:', e);
    }
  };

  const fetchBoxScore = async (game) => {
    // Allow box scores for live games (in progress, halftime, quarter/period breaks) and final games
    const isLiveOrFinal = game.status === 'STATUS_IN_PROGRESS' || 
                         game.status === 'STATUS_FINAL' ||
                         game.status?.includes('HALF') ||
                         (game.status !== 'STATUS_SCHEDULED' && game.status !== 'STATUS_FINAL');
    
    if (!isLiveOrFinal) return;

    setBoxLoading(true);
    setBoxScore(null);
    setSelectedGame(game);
    setShowBoxModal(true);

    try {
      const sportPath = SPORTS[game.sport];
      const url = `${SUMMARY_BASE}${sportPath}/summary?event=${game.id}`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`Box score fetch failed: ${res.status}`);

      const data = await res.json();
      setBoxScore(data);
    } catch (err) {
      console.error('Box score error:', err);
      setBoxScore({ error: 'Could not load box score. Try again later.' });
    } finally {
      setBoxLoading(false);
    }
  };

  const isSameDay = (d1, d2) => {
    // Ensure both are Date objects
    const date1 = d1 instanceof Date ? d1 : new Date(d1);
    const date2 = d2 instanceof Date ? d2 : new Date(d2);
    
    if (isNaN(date1.getTime()) || isNaN(date2.getTime())) return false;
    
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  const formatDateTime = (d) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const gameDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    let dayLabel = gameDay.getTime() === today.getTime() ? 'Today' :
                   gameDay.getTime() === tomorrow.getTime() ? 'Tomorrow' :
                   d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

    return `${dayLabel} ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  };


  const goToPrevDay = () => {
    const prev = new Date(selectedDate);
    prev.setDate(prev.getDate() - 1);
    setSelectedDate(prev);
  };

  const goToNextDay = () => {
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + 1);
    setSelectedDate(next);
  };

  const goToToday = () => setSelectedDate(new Date());

  const isToday = (d) => {
    const today = new Date();
    return d.getDate() === today.getDate() &&
           d.getMonth() === today.getMonth() &&
           d.getFullYear() === today.getFullYear();
  };

  const formatDateLabel = (d) => {
    const today = new Date();
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // ── DERIVED VALUES ───────────────────────────────────────────────────────
  // For Live and Final tabs, use all unfiltered games
  // For upcoming games (Make Pick tab), use filtered games
  const allGamesForDate = allGamesUnfiltered.filter(g => isSameDay(g.date, selectedDate));
  // Include games that are in progress, halftime, or any non-final/non-scheduled status
  // Helper to check if a soccer game is finished (FT = Full Time)
  const isSoccerFinished = (g) => {
    const isSoccer = g.sport === 'EPL' || g.sport === 'UCL' || g.sport === 'WC';
    return isSoccer && g.statusDetail?.toUpperCase().includes('FT');
  };

  // Live games: always from today's games regardless of selected date
  // If browsing a future/past date, todayGamesCache has today's data for the Live tab
  const liveGamesSource = todayGamesCache.length > 0 ? todayGamesCache : allGamesForDate;

  const liveGames = liveGamesSource.filter(g => {
    // If soccer game shows FT, it's finished not live
    if (isSoccerFinished(g)) return false;
    
    const isLive = g.status === 'STATUS_IN_PROGRESS' || 
                   g.status?.includes('HALF') ||
                   (g.status !== 'STATUS_SCHEDULED' && g.status !== 'STATUS_FINAL');
    const hasScore = (g.home.score && g.home.score !== '0') || (g.away.score && g.away.score !== '0');
    return isLive || (hasScore && g.status !== 'STATUS_FINAL');
  });
  const finalGames = allGamesForDate.filter(g => g.status === 'STATUS_FINAL' || isSoccerFinished(g));
  
  // For upcoming games, use the filtered games list
  const gamesForSelectedDate = games.filter(g => isSameDay(g.date, selectedDate));
  const upcomingGames = gamesForSelectedDate.filter(g => g.status === 'STATUS_SCHEDULED');

  // Sport display order - pro sports first, college last
  const SPORT_ORDER = ['NBA', 'NFL', 'NHL', 'UFC', 'EPL', 'UCL', 'WC', 'MLB', 'NCAAB', 'NCAAF'];

  const groupBySport = (gamesList) => gamesList.reduce((acc, game) => {
    if (!acc[game.sport]) acc[game.sport] = [];
    acc[game.sport].push(game);
    return acc;
  }, {});

  // Sort sport entries by priority order
  const sortedSportEntries = (sportObj) => {
    return Object.entries(sportObj).sort(([a], [b]) => {
      const aIndex = SPORT_ORDER.indexOf(a);
      const bIndex = SPORT_ORDER.indexOf(b);
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    });
  };

  const finalGamesBySport = groupBySport(finalGames);
  const liveGamesBySport = groupBySport(liveGames);

  const getFilteredGames = () => {
    if (selectedSport === 'Live' || selectedSport === 'Final') return [];
    if (selectedSport === 'All') return upcomingGames;
    return upcomingGames.filter(g => g.sport === selectedSport);
  };

  const filteredGames = getFilteredGames();

  // Game of the Day - find the closest to 50/50 odds from major sports
  const gameOfTheDay = (() => {
    const majorSportGames = upcomingGames.filter(g => 
      ['NBA', 'NFL', 'NHL', 'UFC'].includes(g.sport) && g.odds
    );
    
    if (majorSportGames.length === 0) return null;
    
    // Find game closest to 50/50
    let bestGame = majorSportGames[0];
    let bestDiff = Math.abs(50 - (bestGame.odds?.homeProb || 50));
    
    for (const game of majorSportGames) {
      const diff = Math.abs(50 - (game.odds?.homeProb || 50));
      if (diff < bestDiff) {
        bestGame = game;
        bestDiff = diff;
      }
    }
    
    return bestGame;
  })();

  // Calculate user's leaderboard position and wins needed to move up
  const leaderboardPosition = (() => {
    if (!user || leaderboard.length === 0) return null;
    
    const userIndex = leaderboard.findIndex(p => p.username === user.username);
    if (userIndex === -1) return { rank: leaderboard.length + 1, winsToMoveUp: null };
    
    const rank = userIndex + 1;
    if (rank === 1) return { rank, winsToMoveUp: null, message: "You're #1! 👑" };
    
    const playerAbove = leaderboard[userIndex - 1];
    const winsNeeded = playerAbove.current_streak - currentStreak + 1;
    
    return {
      rank,
      winsToMoveUp: winsNeeded,
      playerAbove: capitalizeUsername(playerAbove.username),
      message: winsNeeded === 1 
        ? `1 win from passing ${capitalizeUsername(playerAbove.username)}!`
        : `${winsNeeded} wins from #${rank - 1}`
    };
  })();

  // ── EFFECTS ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const savedUser = localStorage.getItem('sftc_session');
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      setUser(userData);
      setUsername(userData.username);
      loadUserData(userData.id);
    }
  }, []);

  useEffect(() => {
    fetchGames();
    const interval = setInterval(() => {
      fetchGames();
    }, 60000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]); // fetchGames is stable and doesn't need to be in deps

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    if (activePicks.length > 0 && allGamesUnfiltered.length > 0 && user) checkPickResult();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allGamesUnfiltered, activePicks]); // checkPickResult and user are stable and don't need to be in deps

  // Poll for game updates every 2 minutes when there's an active pick
  useEffect(() => {
    if (!activePick || !user) return;
    const interval = setInterval(() => {
      fetchGames(selectedDate);
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePick, user, selectedDate]);

  // ── RENDER ───────────────────────────────────────────────────────────────

  return (
    <div className="app">
      <header className="header">
        <div className="header-main">
          <div className="logo">
            <span className="logo-sftc">SFTC</span>
            <span className="logo-sub">Streak For The Cash</span>
          </div>
          {user ? (
            <>
              <div className="token-balance">
                <span className="token-amount">{streakBalance.toLocaleString()}</span>
                <span className="token-label">$STREAK Tokens</span>
              </div>
              <div className="user-info">
                <span>{capitalizeUsername(username)}</span>
                <button onClick={handleLogout}>Logout</button>
              </div>
            </>
          ) : (
            <div className="user-info">
              <button onClick={() => setShowAuthModal(true)} className="login-btn">Login / Sign Up</button>
            </div>
          )}
        </div>
        <nav className="main-nav">
          <span className={`nav-link ${activeTab === 'picks' ? 'active' : ''}`} onClick={() => setActiveTab('picks')}>Make Pick</span>
          <span className={`nav-link ${activeTab === 'leaderboard' ? 'active' : ''}`} onClick={() => setActiveTab('leaderboard')}>Leaderboards</span>
          {user && <span className={`nav-link ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>My Picks</span>}
          <span className={`nav-link ${activeTab === 'rules' ? 'active' : ''}`} onClick={() => setActiveTab('rules')}>How To Play</span>
        </nav>
      </header>

      {user && (
      <div className="streak-banner">
        <div className="streak-item"><span className="streak-num">{currentStreak}</span><span className="streak-lbl">Current Streak</span></div>
        <div className="streak-item"><span className="streak-num">{longestStreak}</span><span className="streak-lbl">Longest</span></div>
        <div className="streak-item"><span className="streak-num">{monthlyRecord.wins}-{monthlyRecord.losses}</span><span className="streak-lbl">Month W-L</span></div>
        <div className="streak-item"><span className="streak-num">{allTimeRecord.wins}-{allTimeRecord.losses}</span><span className="streak-lbl">All Time W-L</span></div>
        <div className="streak-item reward-preview"><span className="streak-num">+{calculateReward(currentStreak)}</span><span className="streak-lbl">Next Win</span></div>
      </div>
      )}

      <main className="main-content">
        {activeTab === 'picks' && (
          <>
            {!user && (
              <div className="guest-notice">
                <strong>👋 Welcome, Guest!</strong> You can browse games and leaderboards, but you need to <button className="inline-link" onClick={() => setShowAuthModal(true)}>login or sign up</button> to make picks and build your streak.
              </div>
            )}
            {user && activePicks.length > 0 && (
              <div className="current-pick-box">
                <div className="cpb-left">
                  {activeParlayAllLegs.length <= 1 ? (
                    <>
                      <span className="cpb-label">YOUR SELECTION:</span>
                      <strong>{activePicks[0].picked_team_name}</strong> vs {activePicks[0].opponent_team_name}
                      <span className="cpb-time">{formatDateTime(new Date(activePicks[0].game_time))}</span>
                    </>
                  ) : (
                    <>
                      <span className="cpb-label">YOUR {activeParlayAllLegs.length}-LEG PARLAY:</span>
                      {activeParlayAllLegs.map((p, i) => (
                        <span key={p.id} className="cpb-parlay-leg">
                          {p.status === 'won' ? '✅' : p.status === 'lost' ? '❌' : '⏳'}
                          {' '}{i + 1}. <strong>{p.picked_team_name}</strong> vs {p.opponent_team_name}
                        </span>
                      ))}
                    </>
                  )}
                </div>
                <div className="cpb-right">
                  {isPickLocked() ? <span className="cpb-locked">🔒 LOCKED - Game Started</span> : <button className="cpb-change" onClick={cancelPick}>Cancel</button>}
                </div>
              </div>
            )}

            {/* Parlay slip - live view of active picks */}
            {user && activePicks.length >= 2 && !isPickLocked() && (
              <div className="parlay-slip">
                <div className="parlay-slip-header">
                  🎲 {activePicks.length}-LEG PARLAY
                  <span className="parlay-bonus">+{activePicks.length} streak if all win</span>
                </div>
                {activePicks.map((p, i) => (
                  <div key={p.id} className="parlay-slip-leg">
                    <span>{i + 1}. <strong>{p.picked_team_name}</strong> vs {p.opponent_team_name}</span>
                    <button className="parlay-remove" onClick={() => handlePick(
                      { id: p.game_id, sport: p.sport, date: new Date(p.game_time), home: { name: '' }, away: { name: '' } },
                      { id: p.picked_team_id, name: p.picked_team_name },
                      false
                    )}>✕</button>
                  </div>
                ))}
                <div className="parlay-slip-actions">
                  <button className="btn-cancel" onClick={cancelPick}>Clear All</button>
                </div>
              </div>
            )}

            {user && leaderboardPosition && (
              <div className="leaderboard-position-msg">
                <span className="rank-badge">#{leaderboardPosition.rank}</span>
                {leaderboardPosition.message && <span className="rank-message">{leaderboardPosition.message}</span>}
                {currentStreak >= 5 && <span className="hot-badge">🔥 Hot Streak!</span>}
              </div>
            )}

            {gameOfTheDay && !activePick && isToday(selectedDate) && (
              <div className="game-of-the-day">
                <div className="gotd-header">
                  <span className="gotd-badge">⭐ GAME OF THE DAY</span>
                </div>
                <div className="gotd-matchup">
                  <button
                    className="gotd-team"
                    onClick={() => user ? handlePick(gameOfTheDay, gameOfTheDay.away, false) : setShowAuthModal(true)}
                  >
                    {gameOfTheDay.away.logo && <img src={gameOfTheDay.away.logo} alt="" />}
                    <span className="gotd-team-name">{gameOfTheDay.away.name}</span>
                    <span className="gotd-odds-pct">{Math.round(gameOfTheDay.odds?.awayProb || 50)}%</span>
                  </button>
                  <span className="gotd-vs">VS</span>
                  <button
                    className="gotd-team"
                    onClick={() => user ? handlePick(gameOfTheDay, gameOfTheDay.home, true) : setShowAuthModal(true)}
                  >
                    {gameOfTheDay.home.logo && <img src={gameOfTheDay.home.logo} alt="" />}
                    <span className="gotd-team-name">{gameOfTheDay.home.name}</span>
                    <span className="gotd-odds-pct">{Math.round(gameOfTheDay.odds?.homeProb || 50)}%</span>
                  </button>
                </div>
                <div className="gotd-time">
                  <span className={`sport-tag ${gameOfTheDay.sport.toLowerCase()}`}>{gameOfTheDay.sport}</span>
                  {formatDateTime(gameOfTheDay.date)}
                </div>
              </div>
            )}

            {user && !activePick && !gameOfTheDay && <div className="welcome-msg">Choose a matchup below to start your streak!</div>}

            <div className="date-nav">
              <button className="date-arrow" onClick={goToPrevDay}>←</button>
              <div className="date-display">
                <span className="date-label">{formatDateLabel(selectedDate)}</span>
                <span className="date-full">{selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <button className="date-arrow" onClick={goToNextDay}>→</button>
              {!isToday(selectedDate) && <button className="today-btn" onClick={goToToday}>Today</button>}
            </div>

            <div className="sport-filters">
              {['All', ...Object.keys(SPORTS)].map(s => (
                <button key={s} className={`filter-btn ${selectedSport === s ? 'active' : ''}`} onClick={() => setSelectedSport(s)}>{s}</button>
              ))}
              <button className={`filter-btn live-btn ${selectedSport === 'Live' ? 'active' : ''}`} onClick={() => setSelectedSport('Live')}>Live ({liveGames.length})</button>
              <button className={`filter-btn final-btn ${selectedSport === 'Final' ? 'active' : ''}`} onClick={() => setSelectedSport('Final')}>Final ({finalGames.length})</button>
            </div>

            {selectedSport === 'Live' && (
              <div className="live-container">
                {/* Show user's active pick game first if it's live */}
                {user && activePick && (() => {
                  const myGame = liveGames.find(g => g.id === activePick.game_id);
                  if (!myGame) return null;
                  const pickedTeam = activePick.picked_team_id === myGame.home.id ? myGame.home : myGame.away;
                  const isWinning = parseInt(pickedTeam.score) > parseInt(activePick.picked_team_id === myGame.home.id ? myGame.away.score : myGame.home.score);
                  const isTied = myGame.home.score === myGame.away.score;
                  return (
                    <div className="my-pick-live">
                      <div className="my-pick-header">🎯 YOUR PICK IS LIVE</div>
                      <table className="games-tbl live-tbl my-pick-table">
                        <tbody>
                          <tr className="row-live row-my-pick clickable" onClick={() => fetchBoxScore(myGame)}>
                            <td>
                              <span className={`live-badge ${isWinning ? 'winning' : isTied ? 'tied' : 'losing'}`}>
                                {isWinning ? '✓ WINNING' : isTied ? 'TIED' : 'LOSING'}
                              </span><br />
                              <span className="live-detail">{myGame.statusDetail}</span>
                            </td>
                            <td className="td-teams">
                              <div className={`team-line ${activePick.picked_team_id === myGame.away.id ? 'picked-team' : ''}`}>
                                {myGame.away.logo && <img src={myGame.away.logo} alt="" />}
                                <span>{myGame.away.name}</span><strong>{myGame.away.score}</strong>
                              </div>
                              <div className={`team-line ${activePick.picked_team_id === myGame.home.id ? 'picked-team' : ''}`}>
                                {myGame.home.logo && <img src={myGame.home.logo} alt="" />}
                                <span>{myGame.home.name}</span><strong>{myGame.home.score}</strong>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
                
                {Object.keys(liveGamesBySport).length === 0 ? (
                  <div className="tbl-empty">No live games right now</div>
                ) : (
                  sortedSportEntries(liveGamesBySport).map(([sport, sportGames]) => (
                    <div key={sport} className="sport-section">
                      <div className="sport-header">
                        <span className={`sport-tag ${sport.toLowerCase()}`}>{sport}</span>
                        <span className="sport-count">{sportGames.length} live</span>
                      </div>
                      <table className="games-tbl live-tbl">
                        <thead><tr><th>Status</th><th>Matchup</th></tr></thead>
                        <tbody>
                          {sportGames.map(game => (
                            <tr
                              key={game.id}
                              className={`row-live clickable ${activePick?.game_id === game.id ? 'row-my-pick-in-list' : ''}`}
                              onClick={() => fetchBoxScore(game)}
                            >
                              <td>
                                <span className="live-badge">LIVE</span><br />
                                <span className="live-detail">{game.statusDetail}</span>
                              </td>
                              <td className="td-teams">
                                <div className={`team-line ${activePick?.picked_team_id === game.away.id ? 'picked-team' : ''}`}>
                                  {game.away.logo && <img src={game.away.logo} alt="" />}
                                  <span>{game.away.name}</span><strong>{game.away.score}</strong>
                                </div>
                                <div className={`team-line ${activePick?.picked_team_id === game.home.id ? 'picked-team' : ''}`}>
                                  {game.home.logo && <img src={game.home.logo} alt="" />}
                                  <span>{game.home.name}</span><strong>{game.home.score}</strong>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))
                )}
              </div>
            )}

            {selectedSport === 'Final' && (
              <div className="finals-container">
                {Object.keys(finalGamesBySport).length === 0 ? (
                  <div className="tbl-empty">No completed games yet</div>
                ) : (
                  sortedSportEntries(finalGamesBySport).map(([sport, sportGames]) => (
                    <div key={sport} className="sport-section">
                      <div className="sport-header">
                        <span className={`sport-tag ${sport.toLowerCase()}`}>{sport}</span>
                        <span className="sport-count">{sportGames.length} game{sportGames.length !== 1 ? 's' : ''}</span>
                      </div>
                      <table className="games-tbl finals-tbl">
                        <thead><tr><th>Time</th><th>Matchup</th></tr></thead>
                        <tbody>
                          {sportGames.map(game => (
                            <tr
                              key={game.id}
                              className="row-final clickable"
                              onClick={() => fetchBoxScore(game)}
                            >
                              <td className="td-datetime">{formatDateTime(game.date)}</td>
                              <td className="td-teams">
                                <div className={`team-line ${game.away.winner ? 'winner' : 'loser'}`}>
                                  {game.away.logo && <img src={game.away.logo} alt="" />}
                                  <span>{game.away.name}</span><strong>{game.away.score}</strong>
                                </div>
                                <div className={`team-line ${game.home.winner ? 'winner' : 'loser'}`}>
                                  {game.home.logo && <img src={game.home.logo} alt="" />}
                                  <span>{game.home.name}</span><strong>{game.home.score}</strong>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))
                )}
              </div>
            )}

            {selectedSport !== 'Live' && selectedSport !== 'Final' && (
              <table className="games-tbl">
                <thead>
                  <tr>
                    <th>Start</th>
                    <th>Sport</th>
                    <th>Opponents</th>
                    <th>Status</th>
                    <th>Pick</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan="5" className="tbl-loading">Loading...</td></tr>}
                  {!loading && filteredGames.length === 0 && <tr><td colSpan="5" className="tbl-empty">No upcoming games. Check Live or Final tabs!</td></tr>}
                  {filteredGames.map(game => {
                    const activeLeg = activePicks.find(p => p.game_id === game.id);
                    const isPicked = !!activeLeg;
                    const pickedTeamId = activeLeg?.picked_team_id;
                    const hasActiveLocked = activePicks.length > 0 && isPickLocked();
                    const canPick = user && !hasActiveLocked;
                    const rowClass = [
                      isPicked ? 'row-picked' : '',
                      game.featured || game.isChampionship ? 'row-featured' : ''
                    ].filter(Boolean).join(' ');
                    return (
                      <tr key={game.id} className={rowClass}>
                        <td className="td-datetime">
                          {game.featuredLabel && <div className="featured-label">{game.featuredLabel}</div>}
                          {formatDateTime(game.date)}
                        </td>
                        <td><span className={`sport-tag ${game.sport.toLowerCase()}`}>{game.sport}</span></td>
                        <td className="td-teams">
                          <button
                            className={`team-pick-btn ${pickedTeamId === game.away.id ? 'selected' : ''}`}
                            onClick={() => user ? handlePick(game, game.away, false) : setShowAuthModal(true)}
                            disabled={user && !canPick && !isPicked}
                          >
                            {game.away.logo && <img src={game.away.logo} alt="" />}
                            <span>{game.away.name}</span>
                            {game.odds && (
                              <span className={`team-odds ${game.odds.awayProb > game.odds.homeProb ? 'favorite' : 'underdog'}`}>
                                {game.odds.awayProb > game.odds.homeProb ? '★' : ''} {Math.round(game.odds.awayProb)}%
                              </span>
                            )}
                          </button>
                          <button
                            className={`team-pick-btn ${pickedTeamId === game.home.id ? 'selected' : ''}`}
                            onClick={() => user ? handlePick(game, game.home, true) : setShowAuthModal(true)}
                            disabled={user && !canPick && !isPicked}
                          >
                            {game.home.logo && <img src={game.home.logo} alt="" />}
                            <span>{game.home.name}</span>
                            {game.odds && (
                              <span className={`team-odds ${game.odds.homeProb > game.odds.awayProb ? 'favorite' : 'underdog'}`}>
                                {game.odds.homeProb > game.odds.awayProb ? '★' : ''} {Math.round(game.odds.homeProb)}%
                              </span>
                            )}
                          </button>
                        </td>
                        <td>
                          {isPicked ? <span className="status-pending">Pending</span> :
                           
                           hasActiveLocked ? <span className="status-locked">Locked</span> :
                           <span className="status-open">Open</span>}
                        </td>
                        <td>{(isPicked) && '✓'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}

        {activeTab === 'leaderboard' && (
          <div className="page-box">
            <h2>Leaderboard</h2>
            <table className="lb-tbl">
              <thead><tr><th>Rank</th><th>Player</th><th>Streak</th><th>Longest</th><th>Record</th><th>$STREAK</th></tr></thead>
              <tbody>
                {leaderboard.length === 0 ? <tr><td colSpan="6">No players yet</td></tr> : leaderboard.map((p,i) => (
                  <tr key={i} className={`${i < 3 ? 'top3' : ''} ${p.username === username ? 'is-you' : ''}`}>
                    <td>{i+1}</td>
                    <td>
                      <span className="username-link" onClick={() => openProfile(p.username)}>
                        {capitalizeUsername(p.username)}
                      </span>
                      {p.username === username && <span className="you-badge">(You)</span>}
                      {p.current_streak >= 5 && <span className="hot-streak-badge">🔥</span>}
                    </td>
                    <td className="col-streak">
                      {p.current_streak}
                      {p.current_streak >= 10 && <span className="streak-fire">🔥🔥</span>}
                      {p.current_streak >= 5 && p.current_streak < 10 && <span className="streak-fire">🔥</span>}
                    </td>
                    <td>{p.longest_streak}</td>
                    <td>{p.all_time_record?.wins || 0}-{p.all_time_record?.losses || 0}</td>
                    <td className="col-balance">{(p.streak_balance || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="page-box">
            <h2>My Pick History</h2>
            <div className="stats-row">
              <div className="stat-box"><span className="stat-num">{currentStreak}</span><span>Current</span></div>
              <div className="stat-box"><span className="stat-num">{longestStreak}</span><span>Best</span></div>
              <div className="stat-box"><span className="stat-num">{monthlyRecord.wins}-{monthlyRecord.losses}</span><span>Month</span></div>
              <div className="stat-box"><span className="stat-num">{allTimeRecord.wins}-{allTimeRecord.losses}</span><span>All Time</span></div>
              <div className="stat-box"><span className="stat-num">{streakBalance.toLocaleString()}</span><span>$STREAK</span></div>
            </div>
            <table className="history-tbl">
              <thead><tr><th>Date</th><th>Pick</th><th>vs</th><th>Result</th></tr></thead>
              <tbody>
                {pickHistory.length === 0 ? <tr><td colSpan="4">No picks yet</td></tr> : (() => {
                  // Group parlay legs together
                  const rows = [];
                  const seen = new Set();
                  for (const p of pickHistory) {
                    if (seen.has(p.id)) continue;
                    seen.add(p.id);
                    if (p.parlay_id) {
                      const legs = pickHistory.filter(x => x.parlay_id === p.parlay_id);
                      legs.forEach(l => seen.add(l.id));
                      const allWon = legs.every(l => l.status === 'won');
                      const anyLost = legs.some(l => l.status === 'lost');
                      const parlayStatus = allWon ? 'won' : anyLost ? 'lost' : legs[0].status;
                      rows.push(
                        <tr key={p.parlay_id} className="history-parlay-row">
                          <td>{new Date(p.created_at).toLocaleDateString()}</td>
                          <td colSpan="2">
                            <span className="history-parlay-badge">{legs.length}-LEG PARLAY</span>
                            {legs.map((l, i) => (
                              <div key={l.id} className="history-parlay-leg">
                                <strong>{l.picked_team_name}</strong> vs {l.opponent_team_name}
                              </div>
                            ))}
                          </td>
                          <td><span className={`res-badge ${parlayStatus}`}>{parlayStatus === 'won' ? 'W' : parlayStatus === 'push' ? 'P' : 'L'}</span></td>
                        </tr>
                      );
                    } else {
                      rows.push(
                        <tr key={p.id}>
                          <td>{new Date(p.created_at).toLocaleDateString()}</td>
                          <td><strong>{p.picked_team_name}</strong></td>
                          <td>{p.opponent_team_name}</td>
                          <td><span className={`res-badge ${p.status}`}>{p.status === 'won' ? 'W' : p.status === 'push' ? 'P' : 'L'}</span></td>
                        </tr>
                      );
                    }
                  }
                  return rows;
                })()}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'rules' && (
          <div className="page-box">
            <h2>How To Play</h2>
            <h3>🎯 The Goal</h3>
            <p>Build the longest winning streak by picking game winners across any sport. No spreads, no point totals — just pick who wins.</p>

            <h3>📋 The Basics</h3>
            <ul>
              <li><strong>One pick at a time.</strong> Once your game ends, you can pick again.</li>
              <li><strong>Change your pick</strong> anytime before the game goes live.</li>
              <li><strong>Win = streak grows.</strong> Lose = streak resets to zero.</li>
              <li><strong>Multiple picks per day</strong> as long as you keep winning!</li>
            </ul>

            <h3>🎲 Parlays</h3>
            <p>Want to go big? Select 2–4 games to build a parlay. All legs must win — but if they do, your streak grows by the number of legs.</p>
            <ul>
              <li><strong>3-leg parlay, all win</strong> → streak +3</li>
              <li><strong>Any leg loses</strong> → streak resets to zero</li>
              <li><strong>Postponed/void leg</strong> → drops out, remaining legs still must win</li>
              <li>All picks must be on the same day. Click any leg to remove it.</li>
            </ul>

            <h3>🤖 Beat the Supercomputer</h3>
            <p>Our AI Supercomputer makes 3–5 picks every day using advanced odds data. If your streak surpasses the Supercomputer's on the leaderboard, you beat the machine. Think you can outpick an algorithm? Prove it.</p>

            <h3>🪂 $STREAK Tokens</h3>
            <ul>
              <li><strong>Sign up bonus:</strong> {SIGNUP_BONUS} $STREAK</li>
              <li><strong>Win reward:</strong> {WIN_BASE_REWARD} $STREAK base + {STREAK_BONUS_PER_LEVEL} per streak level (up to +{MAX_STREAK_BONUS})</li>
              <li><strong>Example:</strong> Win at a 10-streak = {WIN_BASE_REWARD + 10} $STREAK</li>
              <li><strong>Parlay wins</strong> pay per winning leg</li>
            </ul>
            <p className="airdrop-info">Early players will receive a future airdrop based on their $STREAK balance. Keep playing and stacking!</p>

            <h3>🏆 Leaderboard</h3>
            <p>Longest streak at month's end wins bragging rights. Top players are ranked live — see where you stack up.</p>
          </div>
        )}
      </main>

      {/* Pick confirmation modal removed — picks save instantly on click */}

      {showBoxModal && (
        <div className="modal-bg" onClick={() => setShowBoxModal(false)}>
          <div className="modal-box box-modal" onClick={e => e.stopPropagation()}>
            <h3>Box Score: {selectedGame?.away.name} @ {selectedGame?.home.name}</h3>

            {boxLoading ? (
              <p>Loading box score...</p>
            ) : boxScore?.error ? (
              <p className="error">{boxScore.error}</p>
            ) : boxScore?.boxscore ? (
              <div className="boxscore-content">
                {boxScore.boxscore.teams?.map((teamBox, teamIdx) => (
                  <div key={teamIdx} className="team-box">
                    <h4>{teamBox.team.displayName} ({teamBox.team.abbreviation})</h4>

                    {/* Team totals */}
                    {teamBox.statistics && (
                      <div className="team-totals">
                        {teamBox.statistics.map((stat, sIdx) => (
                          <span key={sIdx}>
                            {stat.label || stat.name}: {stat.displayValue || stat.value}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* All player statistic categories for this team */}
                    {boxScore.boxscore.players
                      ?.filter(playerTeam => playerTeam.team?.id === teamBox.team.id)
                      ?.flatMap(playerTeamGroup =>
                        (playerTeamGroup.statistics || []).map((category, catIdx) => {
                          if (!category.athletes?.length) return null;

                          return (
                            <div key={catIdx} className="stat-category">
                              <h5>{category.text || category.name || 'Player Stats'}</h5>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Player</th>
                                    {category.labels?.map((label, i) => (
                                      <th key={i}>{label}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {category.athletes.map((athleteEntry, athIdx) => (
                                    <tr key={athIdx}>
                                      <td>
                                        {athleteEntry.athlete.displayName}
                                        {athleteEntry.athlete.jersey ? ` #${athleteEntry.athlete.jersey}` : ''}
                                      </td>
                                      {athleteEntry.stats?.map((val, statIdx) => (
                                        <td key={statIdx}>{val || '-'}</td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        })
                      )}
                  </div>
                ))}
              </div>
            ) : (
              <p>No box score available yet.</p>
            )}

            <div className="modal-btns">
              <button className="btn-cancel" onClick={() => setShowBoxModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showAuthModal && (
        <div className="modal-bg" onClick={() => { setShowAuthModal(false); setAuthError(''); }}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>{authMode === 'login' ? 'Sign In' : 'Create Account'}</h3>
            <form onSubmit={handleAuth}>
              <div className="form-row">
                <label>Username:</label>
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="Enter username"
                />
              </div>
              <div className="form-row">
                <label>Password:</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter password"
                />
              </div>
              {authError && <div className="auth-error">{authError}</div>}
              <button type="submit" className="auth-btn" disabled={authLoading}>
                {authLoading ? 'Loading...' : (authMode === 'login' ? 'Sign In' : 'Sign Up')}
              </button>
            </form>
            <div className="auth-switch">
              {authMode === 'login' ? (
                <p>Don't have an account? <button onClick={() => setAuthMode('signup')}>Sign Up Free</button></p>
              ) : (
                <p>Have an account? <button onClick={() => setAuthMode('login')}>Sign In</button></p>
              )}
            </div>
            <div className="modal-btns">
              <button className="btn-cancel" onClick={() => { setShowAuthModal(false); setAuthError(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showProfileModal && (
        <div className="modal-bg" onClick={closeProfile}>
          <div className="modal-box profile-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={closeProfile}>×</button>
            
            {profileLoading ? (
              <p className="loading-text">Loading profile...</p>
            ) : profileData ? (
              <>
                <div className="profile-header">
                  <h2>{capitalizeUsername(profileData.username)}</h2>
                  <span className="profile-joined">Member since {new Date(profileData.created_at).toLocaleDateString()}</span>
                </div>

                <div className="profile-stats">
                  <div className="stat-box">
                    <span className="stat-num">{profileData.current_streak}</span>
                    <span>Current</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-num">{profileData.longest_streak}</span>
                    <span>Best</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-num">{profileData.monthly_wins || 0}-{profileData.monthly_losses || 0}</span>
                    <span>Month</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-num">{profileData.all_time_wins || 0}-{profileData.all_time_losses || 0}</span>
                    <span>All Time</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-num">{(profileData.streak_balance || 0).toLocaleString()}</span>
                    <span>$STREAK</span>
                  </div>
                </div>

                <div className="profile-section">
                  <h3>Recent Picks</h3>
                  {profilePicks.length === 0 ? (
                    <p className="no-data">No picks yet</p>
                  ) : (
                    <table className="profile-picks-tbl">
                      <thead>
                        <tr><th>Date</th><th>Pick</th><th>vs</th><th>Result</th></tr>
                      </thead>
                      <tbody>
                        {profilePicks.slice(0, 10).map((p, i) => (
                          <tr key={i}>
                            <td>{new Date(p.created_at).toLocaleDateString()}</td>
                            <td><strong>{p.picked_team_name}</strong></td>
                            <td>{p.opponent_team_name}</td>
                            <td><span className={`res-badge ${p.status}`}>{p.status === 'won' ? 'W' : p.status === 'push' ? 'P' : 'L'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="profile-section">
                  <h3>Guestbook</h3>
                  
                  {user && user.username !== profileData.username && (
                    <div className="guestbook-form">
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={`Leave a message for ${capitalizeUsername(profileData.username)}...`}
                        maxLength={500}
                      />
                      <button onClick={submitMessage} disabled={!newMessage.trim()}>Post Message</button>
                    </div>
                  )}

                  {!user && (
                    <p className="login-prompt">Sign in to leave a message</p>
                  )}

                  <div className="guestbook-messages">
                    {profileMessages.length === 0 ? (
                      <p className="no-data">No messages yet. Be the first!</p>
                    ) : (
                      profileMessages.map((msg, i) => (
                        <div key={i} className="guestbook-msg">
                          <div className="msg-header">
                            <span className="msg-author" onClick={() => { closeProfile(); setTimeout(() => openProfile(msg.author_username), 100); }}>
                              {capitalizeUsername(msg.author_username)}
                            </span>
                            <span className="msg-date">{new Date(msg.created_at).toLocaleDateString()}</span>
                          </div>
                          <p className="msg-text">{msg.message}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            ) : (
              <p className="error-text">Profile not found</p>
            )}
          </div>
        </div>
      )}

      <footer className="footer">SFTC © 2026 | $STREAK tokens are for entertainment purposes</footer>

      {/* ADMIN ONLY - bulk settle pending picks */}
      {user?.id === 'fa233083-1aa9-4419-90e6-87f8ca1b9397' && (
        <div style={{ textAlign: 'center', padding: '8px', opacity: 0.4 }}>
          <button onClick={bulkSettlePicks} style={{ fontSize: '11px', padding: '4px 10px', background: '#333', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            🔧 Admin: Bulk Settle
          </button>
        </div>
      )}

      <Analytics />
    </div>
  );
}